import { useEffect, useMemo, useState } from 'react';
import { CortexDialog, CortexDialogBody, CortexDialogFooter } from '../dialogs/CortexDialog';
import { Button } from '@coordio/ui';
import { LoadingSkeleton } from '@coordio/ui/layout';
import { Download as DownloadIcon } from 'lucide-react';
import type { AgentSessionFileDto } from '../../types/file';
import { fileService } from '../../services/fileService';
import { useNotificationStore } from '../../store/notificationStore';

export interface FilePreviewProps {
  open: boolean;
  file: AgentSessionFileDto | null;
  onClose: () => void;
}

/**
 * Modal preview for a file (images, PDFs, and text).
 */
export const FilePreview = ({ open, file, onClose }: FilePreviewProps) => {
  const { addNotification } = useNotificationStore();

  const [loading, setLoading] = useState(false);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [text, setText] = useState<string | null>(null);

  const contentType = file?.contentType || 'application/octet-stream';

  const kind = useMemo(() => {
    if (!file) return 'unknown';
    if (contentType.startsWith('image/')) return 'image';
    if (contentType === 'application/pdf') return 'pdf';
    if (contentType.startsWith('text/') || contentType.includes('json') || contentType.includes('xml'))
      return 'text';
    return 'unknown';
  }, [file, contentType]);

  useEffect(() => {
    if (!open || !file) return;

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setText(null);

      try {
        const resp = await fileService.downloadFileBlob(file.id);
        if (cancelled) return;

        const url = URL.createObjectURL(resp.data);
        setBlobUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return url;
        });

        if (kind === 'text') {
          const t = await resp.data.text();
          if (!cancelled) setText(t);
        }
      } catch (err: any) {
        const msg = err.response?.data?.message || err.message || 'Failed to load file preview';
        addNotification(msg, 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [open, file?.id, kind]);

  const handleDownload = () => {
    if (!blobUrl || !file) return;
    try {
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = file.fileName || 'download';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e: any) {
      addNotification(e?.message || 'Failed to download file', 'error');
    }
  };

  const handleClose = () => {
    setText(null);
    setBlobUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    onClose();
  };

  return (
    <CortexDialog open={open} onClose={handleClose} title={file?.fileName || 'File preview'} size="xl" data-testid="file-preview-dialog">
      <CortexDialogBody>
        {loading ? (
          <div className="flex justify-center py-4">
            <LoadingSkeleton />
          </div>
        ) : !file ? (
          <p className="text-sm text-muted-foreground">No file selected.</p>
        ) : !blobUrl ? (
          <p className="text-sm text-muted-foreground">Preview unavailable.</p>
        ) : kind === 'image' ? (
          <div className="flex justify-center">
            <img
              src={blobUrl}
              alt={file.fileName}
              style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
            />
          </div>
        ) : kind === 'pdf' ? (
          <iframe
            title={file.fileName}
            src={blobUrl}
            style={{ width: '100%', height: '70vh', border: 0 }}
          />
        ) : kind === 'text' ? (
          <pre className="m-0 p-2 rounded bg-muted overflow-auto max-h-[70vh] text-xs">
            {text ?? '(loading text...)'}
          </pre>
        ) : (
          <p className="text-sm text-muted-foreground">
            No inline preview available for content type: {contentType}
          </p>
        )}
      </CortexDialogBody>
      <CortexDialogFooter>
        <Button variant="ghost" onClick={handleClose}>Close</Button>
        <Button
          onClick={handleDownload}
          disabled={!blobUrl || !file}
        >
          <DownloadIcon className="mr-2 h-4 w-4" />
          Download
        </Button>
      </CortexDialogFooter>
    </CortexDialog>
  );
};
